/**
 * useCreateProperty — POST `/organizations/:slug/properties`.
 *
 * The mutation backs the Create wizard's Confirm step. On success
 * the property's detail page receives focus via `onSuccess` returning
 * the created id; the wizard's caller (PropertyCreateWizardPage)
 * routes to `/properties/{id}`.
 *
 * Form state lives in the wizard component (local React state); the
 * hook is shape-agnostic and accepts the full `CreatePropertyDto`
 * the BE expects. The wizard's reverse mapper (form → DTO) lives in
 * `_components/PropertyCreateWizard.tsx` next to the form state.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { components } from '@artemis/contracts';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import { propertyQueryKeys } from './queryKeys.js';

type CreatePropertyDto = components['schemas']['CreatePropertyDto'];
type PropertyResponseDto = components['schemas']['PropertyResponseDto'];

export function useCreateProperty(): {
  readonly mutateAsync: (body: CreatePropertyDto) => Promise<string>;
  readonly status: 'idle' | 'pending' | 'success' | 'error';
  readonly errorMessage: string | null;
  readonly reset: () => void;
} {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;
  const queryClient = useQueryClient();

  const mutation = useMutation<string, Error, CreatePropertyDto>({
    mutationFn: async (body) => {
      if (orgSlug === null) {
        throw new Error('Cannot create a property without an org slug');
      }
      const result = await apiClient.POST(
        '/organizations/{slug}/properties',
        {
          params: { path: { slug: orgSlug } },
          body,
        },
      );
      if (!result.response.ok || result.data === undefined) {
        throw new Error(
          `POST /organizations/${orgSlug}/properties failed with status ${result.response.status}`,
        );
      }
      // Invalidate the list so the new property appears.
      await queryClient.invalidateQueries({
        queryKey: propertyQueryKeys.lists(),
      });
      return (result.data as PropertyResponseDto).id;
    },
  });

  return {
    mutateAsync: async (body) => mutation.mutateAsync(body),
    status: mutation.status,
    errorMessage:
      mutation.status === 'error' && mutation.error !== null
        ? mutation.error.message
        : null,
    reset: mutation.reset,
  };
}
